import '../../../scss/pages/admin/_admin-clients.scss';

import {
  DatePicker,
  DialogActionButton,
  saveCallbackStatus,
  Status,
  TextField,
} from '@finpay-development/shared-components';
import { Divider, MenuItem, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import {useFormik} from 'formik';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { createCommentSuccesMessages } from '@finpay-development/shared-platform/lib/esm/shared/enums';
import { RootState } from '../../../shared/state/root-reducer';
import { AppDispatch } from '../../../shared/state/store';
import { commonFields } from '@finpay-development/shared-platform/lib/esm/shared/validation/schemas';
import { getPatientStatusByStatusId, StatusType } from '@finpay-development/shared-platform/lib/esm/patient/components/misc/patient-status';
import { createPatientNote, getInstanceOfCare } from '@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk';
import { emptyPatientNote, PatientNotes } from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-note';
import { workFlowStatus} from '@finpay-development/shared-platform/lib/esm/patient/components/models/workflow-status';
import {emptyWorkflow, Workflow} from '@finpay-development/shared-platform/lib/esm/shared/model/client';
import { paynowService as paymentService } from "@finpay-development/shared-platform/lib/esm/guest/services/paynow-service";


interface ChangeStatusModalProps {
  open: boolean;
  handleModalCancel: () => void;
  handleModalSubmit: (obj: {
    workFlow: Workflow;
    encounterId: number;
    patientId: number
  }) => void;
}

interface ChangeStatusModalFormikValues {
  workflow: Workflow,
  noteText: string,
  noteDt: string,
  completionDueDt: string
}

export function ChangeStatusModal(props: ChangeStatusModalProps) {
  const { open, handleModalCancel, handleModalSubmit } = props;
  const [enableSaveButton, setEnableSaveButton] = useState(false)
  const [hideOnHoldDate, setHideOnHoldDate] = useState(true)
  const isValueSelected = false;
  const formRef: any = useRef();
  const dispatch = useDispatch<AppDispatch>();

  const selectors = {
    selectedEncounter: useSelector((store: RootState) => {
        return store.patientContext?.selectedEncounter;
    }),
    currentPaymentProgramId: useSelector(
      (state: RootState) => state.patientContext.selectedEncounter.patientPaymentProgram[0]?.patientPaymentProgramId
    ),
  }
  const { selectedEncounter, currentPaymentProgramId } = selectors;


  function handleCancelCallback() {
    handleModalCancel();
  }

  const initialValues: ChangeStatusModalFormikValues = {
    workflow: emptyWorkflow,
    noteText: '',
    noteDt:"",
    completionDueDt: ""
  };

  const validationSchema = Yup.object(
    {
      noteText: Yup.string().required(),
      workflow: Yup.object().test('validate ', (value:any) => {
      return value.workflowId !== 0;
    }),
    completionDueDt: Yup.string().when("workflow", {
      is: (val: any) => {
        return val?.workflowSubStatus?.workflowSubStatusId === 26 // Date is required if you choose At Risk: On Hold
      },
      then: commonFields.REQUIRED_STRING,
      otherwise: Yup.string()
    })
    }
  );
  function getDateInFuture(days: number) {
    let todayFullDate = new Date();
    const todayDayOfMonth = todayFullDate.getDate();
    return todayFullDate.setDate(todayDayOfMonth + days);
  }

  function checkIfIsValid(value: {}) {
    validationSchema
      .validate(value)
      .then(() => {
        setEnableSaveButton(true);
      })
      .catch(() => {
        setEnableSaveButton(false);
      });
  }

  const formik = useFormik({
    innerRef: formRef,
    initialValues: initialValues,
    validationSchema: validationSchema,
    validate: checkIfIsValid,
    onSubmit: () => {}
  })

  async function handleSave() {
    let isReminder = !hideOnHoldDate
    const currentDate = new Date();
    const noteDt = currentDate.toISOString()
    let completionDueDt = formik.values?.completionDueDt;

    let workFlow = formik.values.workflow
    let noteText = formik.values.noteText;
    let encounterId = selectedEncounter?.patientEncounterId;
    let patientId = selectedEncounter?.patientId;
    let payloadToSave = {workFlow, encounterId, patientId};
    let changeStatusComment: PatientNotes = emptyPatientNote
    if (noteText){
      if (isReminder) {
        if (completionDueDt){
          workFlow.workflowStatusDt = new Date(completionDueDt).toISOString();
          const completionDueDtFormatted= new Date(completionDueDt)
          changeStatusComment.completionDueDt = completionDueDtFormatted.toISOString()
        } else {
          const newDate = new Date();
          newDate.setDate(newDate.getDate() + 30);
          changeStatusComment.completionDueDt = newDate.toISOString()
        }
      }
      changeStatusComment.noteDt = noteDt;
      changeStatusComment.isReminder = isReminder;
      changeStatusComment.noteText = noteText;
      changeStatusComment.patientEncounterId = encounterId;
      changeStatusComment.patientId = patientId;

      dispatch(createPatientNote({message: createCommentSuccesMessages.statusChangeComment, comment: changeStatusComment}))
    }
    await handleModalSubmit(payloadToSave);

    if (currentPaymentProgramId != null) {
      await paymentService.updatePaymentProgram(currentPaymentProgramId);
    }
    // change status endpoint returns an incomplete workflow object. need to re-fetch the IOC upon changing status.
    await dispatch(getInstanceOfCare({patientId: patientId, encounterId: encounterId}))
  }

  const warningMessage = selectedEncounter?.workflow?.workflowSubStatus?.workflowSubStatusId === 30 &&
  formik.values?.workflow?.workflowStatus?.workflowStatusId === 8 ?
        "Are you sure your documents have been signed and uploaded?" : undefined

  return (
    <Dialog
      scroll="body"
      className="modal client-modal"
      open={open}
      fullWidth={true}
      maxWidth="md"
    >
      <DialogTitle>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item xs={12}>
            <span className="subtitle">Change Status</span>
          </Grid>

        </Grid>
      </DialogTitle>
      <DialogContent>
          <Grid container spacing={2}>
            <Grid xs={4} item>
              <Typography variant="body1">Current Workflow Status:</Typography>
            </Grid>
            <Grid xs={8} item>
              <Status
                typographyVariant="h3"
                text={`${selectedEncounter?.workflow?.workflowStatus?.workflowStatusDesc}${selectedEncounter?.workflow?.workflowSubStatus?.workflowSubStatusDesc && ': '}
                    ${selectedEncounter?.workflow?.workflowSubStatus?.workflowSubStatusDesc && selectedEncounter?.workflow?.workflowSubStatus?.workflowSubStatusDesc}`}
                statusColor={
                  selectedEncounter?.workflow?.workflowSubStatus?.workflowSubStatusDesc === "None" ?
                  getPatientStatusByStatusId(
                      selectedEncounter?.workflow?.workflowStatus?.workflowStatusId,
                      StatusType["primary-status"],
                      selectedEncounter?.workflow?.workflowId,
                  ).color :
                  getPatientStatusByStatusId(
                    selectedEncounter?.workflow?.workflowSubStatus?.workflowSubStatusId,
                    StatusType["sub-status"],
                    selectedEncounter?.workflow?.workflowId,
                  ).color}
              />
            </Grid>
            <Grid xs={12} item className="mb-1">
              <Divider />
            </Grid>
            <Grid xs={12} item className="mb-2">
              <TextField
                select={true}
                error={
                  isValueSelected && formik.touched.workflow && formik.errors?.workflow
                }
                label="Change Payment Workflow Status"
                name="workflow"
                required={true}
                value={formik.values.workflow as unknown as number} //todo : need to update TextField component to accept objects
                onChange={(e: any) => {
                  formik.handleChange(e);
                  formik.setFieldValue("workflow",e.target.value)
                  if(e.target.value.workflowStatus.workflowStatusId === 10 &&
                     e.target.value.workflowSubStatus.workflowSubStatusId === 26
                    ){
                      setHideOnHoldDate(false)
                    } else {
                      setHideOnHoldDate(true)
                    }
                }}
                onBlur={formik.handleBlur}
              >
                <MenuItem disabled value="0">Select Payment Workflow Status</MenuItem>
                {workFlowStatus[selectedEncounter?.workflow?.workflowId === 3 ? 'Converted' : 'preconvert'].map((workflowStatus: any) => {
                  return (
                    <MenuItem key={workflowStatus.workflowSubStatus.workflowSubStatusId} value={workflowStatus}>
                      {`${workflowStatus.workflowStatus.workflowStatusName}${workflowStatus.workflowSubStatus?.workflowSubStatusName && ': '}
                        ${workflowStatus.workflowSubStatus?.workflowSubStatusName && workflowStatus.workflowSubStatus.workflowSubStatusName}`}
                    </MenuItem>
                  )
                })}
              </TextField>
            </Grid>
            <Grid xs={4} hidden={hideOnHoldDate} item className="mb-2">
              <DatePicker
              minDate= {getDateInFuture(1)}
              className="m-0"
              label="Remind On Date"
              value={formik.values.completionDueDt}
              onChange={(date: Date) => {
                formik.setFieldValue('completionDueDt', date)
              }}
              />
            </Grid>
            <Grid xs={12} item>
              <TextField
                error={
                  formik.touched["noteText"] && formik.errors["noteText"]
                }
                label="Comments"
                name="noteText"
                multiline={true}
                value={formik.values.noteText}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required={true}
              />
            </Grid>
          </Grid>
      </DialogContent>
      <DialogActions>
        {warningMessage && (
            <div
                className="body-small"
                style={{
                  color: 'red',
                  lineHeight: '1.5'
            }}
                test-id="change-status-modal-warning-msg"
            >
              {warningMessage}
            </div>
        )}
        <DialogActionButton
          isEnabled={enableSaveButton}
          savebuttonText='Save'
          saveStatus={saveCallbackStatus.none}
          executeSave={handleSave}
          handleCallbackSave={() => {}}
          handleCallbackCancel={handleCancelCallback}
        />
      </DialogActions>
    </Dialog>
  );
}